/**
 * Coalesces many "please redraw" requests into at most one callback per animation
 * frame, and guarantees the loop stops when nothing is dirty. This is the single
 * place allowed to call requestAnimationFrame for world rendering.
 */
export class FrameScheduler {
  private handle: number | null = null;
  private readonly callbacks = new Set<() => void>();

  constructor(
    private readonly raf: (cb: () => void) => number = (cb) => globalThis.requestAnimationFrame(cb),
    private readonly caf: (id: number) => void = (id) => {
      globalThis.cancelAnimationFrame(id);
    },
  ) {}

  /** Runs `callback` on the next frame. Duplicate references are deduplicated. */
  schedule(callback: () => void): void {
    this.callbacks.add(callback);
    if (this.handle !== null) return;
    this.handle = this.raf(() => {
      this.flush();
    });
  }

  get isPending(): boolean {
    return this.handle !== null;
  }

  cancel(): void {
    if (this.handle !== null) this.caf(this.handle);
    this.handle = null;
    this.callbacks.clear();
  }

  private flush(): void {
    this.handle = null;
    const batch = [...this.callbacks];
    this.callbacks.clear();
    for (const cb of batch) cb();
  }
}
